import { useMany, useTranslate } from '@refinedev/core';
import { useTable } from '@refinedev/antd';
import { Card, Flex, Progress, Table, Typography, theme } from 'antd';
import type {
  PublicationListOut,
  SessionEntitlementOut,
  SessionListOut,
} from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash } from '@/components/common/show/shared';
import {
  ProductFormatTag,
  ProductVariantTag,
} from '@/components/progress/tags';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { formatDate, formatDateTime, formatSecondsClock } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';

const { Text } = Typography;

export const ProfileSessionsTab = ({ profileId }: { profileId: number }) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { tableProps, sorters, result } = useTable<SessionListOut>({
    resource: 'progress.sessions',
    queryOptions: { enabled: profileId != null },
    filters: {
      permanent: [{ field: 'profile_id', operator: 'eq', value: profileId }],
    },
    sorters: { initial: [{ field: 'updated_at', order: 'desc' }] },
    pagination: { pageSize: 10 },
    syncWithLocation: false,
  });

  const publicationIds = [
    ...new Set(
      (result?.data ?? []).map((r) => r.publication_id).filter(Boolean)
    ),
  ];
  const { result: publicationsResult } = useMany<PublicationListOut>({
    resource: 'user_catalog.publications',
    ids: publicationIds,
    queryOptions: { enabled: publicationIds.length > 0 },
  });
  const publicationMap = new Map<number, PublicationListOut>(
    (publicationsResult?.data ?? []).map((p: PublicationListOut) => [p.id, p])
  );

  return (
    <Card
      title={`${t('account.profiles.tabs.sessions')} (${result?.total ?? 0})`}
    >
      <Table<SessionListOut>
        {...tableProps}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
        size="small"
      >
        <Table.Column<SessionListOut>
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
          render={(value: number) => (
            <ShowIconLink resource="progress.sessions" id={value}>
              {value}
            </ShowIconLink>
          )}
        />
        <Table.Column<SessionListOut>
          dataIndex="publication_id"
          title={t('progress.sessions.fields.publication_id')}
          render={(value: number) => (
            <PublicationInfoPopover
              publication={publicationMap.get(value)}
              publicationId={value}
            />
          )}
        />
        <Table.Column<SessionListOut>
          dataIndex="product_kind"
          title={t('progress.sessions.fields.product_kind')}
          render={(value: string) =>
            orDash(
              value && <ProductFormatTag value={value} labels="sessions" />
            )
          }
        />
        <Table.Column<SessionListOut>
          dataIndex="product_variant"
          title={t('progress.sessions.fields.product_variant')}
          render={(value: string) =>
            orDash(
              value && <ProductVariantTag value={value} labels="sessions" />
            )
          }
        />
        <Table.Column<SessionListOut>
          dataIndex="covered_seconds"
          title={t('progress.sessions.fields.covered_seconds')}
          sorter
          sortOrder={getSortOrder(sorters, 'covered_seconds')}
          render={(value: number, record) => (
            <Flex vertical gap={2} style={{ minWidth: 130 }}>
              <Progress
                percent={
                  record.product_duration > 0
                    ? Math.round((value / record.product_duration) * 100)
                    : 0
                }
                size="small"
                style={{ marginBottom: 0 }}
              />
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {formatSecondsClock(value, { alwaysHours: true })}
              </Text>
            </Flex>
          )}
        />
        <Table.Column<SessionListOut>
          dataIndex="entitlement"
          title={t('progress.sessions.fields.entitlement')}
          render={(value: SessionEntitlementOut | null) =>
            orDash(
              value &&
                `${formatDate(value.valid_from)} → ${formatDate(value.valid_until)}`
            )
          }
        />
        <Table.Column<SessionListOut>
          dataIndex="updated_at"
          title={t('base.fields.updated_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'updated_at')}
          render={(value: string) => orDash(formatDateTime(value))}
        />
      </Table>
    </Card>
  );
};
